import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import type { CancelTransactionRequest } from '@leanpay/models'
import { logoutUser } from '@leanpay/services'
import {
    Button,
    CloseCircleIcon,
    Dialog,
    DialogClose,
    DialogContent,
    DialogTrigger,
    Progress,
    ThinkingFaceImg,
} from '@leanpay/ui'

import { queryClient } from 'app/app'
import { useAppStore } from 'app/app-store'
import { useCancelTransaction } from 'library/hooks/query-hooks'
import { shouldShowProgressBar } from 'library/utils'

interface CheckoutHeaderProps {
    progress: number
    showCancelIcon?: boolean
    children: React.ReactNode
}

const CheckoutHeader = ({ progress, showCancelIcon = false, children }: CheckoutHeaderProps) => {
    const { t } = useTranslation()
    const { vendorToken, resetStore } = useAppStore()
    const navigate = useNavigate()
    const showProgressbar = shouldShowProgressBar()
    const [isOpen, setIsOpen] = useState(false)

    const { mutateAsync: mutateCancelTransaction } = useCancelTransaction()

    const handleExitCheckoutProcess = async () => {
        const request: CancelTransactionRequest = {
            surrogateId: null,
            token: vendorToken,
            maturityPriceId: null,
            maturityGraceId: null,
            firstPaymentDate: null,
        }

        try {
            await mutateCancelTransaction(request)
            resetStore()
            logoutUser()
            queryClient.clear()
            window.location.assign('https://www.leanpay.ro')
        } catch {
            navigate('/', { replace: true })
        }
    }

    return (
        <>
            {showProgressbar && (
                <div
                    className="flex flex-col max-w-[840px] w-full sticky items-center px-2 pt-1 z-10 rounded-lg max-[766px]:w-full max-[766px]:rounded-none min-[767px]:max-[899px]:rounded-none min-[767px]:max-[899px]:w-full"
                    style={{
                        background:
                            'linear-gradient(0deg, rgba(2,0,36,0) 0%, rgba(243,246,248,1) 58%)',
                    }}
                >
                    <Dialog onOpenChange={setIsOpen}>
                        <Progress value={progress} className="w-full" />
                        <DialogTrigger asChild>
                            <button
                                className={`pt-2 cursor-pointer self-end bg-transparent ${showCancelIcon && !isOpen ? 'block' : 'hidden'}`}
                                aria-label={t('common.leave')}
                            >
                                <CloseCircleIcon />
                            </button>
                        </DialogTrigger>

                        <DialogContent>
                            <div className="p-4 pt-12 self-stretch text-center">
                                <img src={ThinkingFaceImg} alt="Thinking" className="mx-auto" />

                                <p
                                    className="text-base mb-10 mt-8"
                                    dangerouslySetInnerHTML={{
                                        __html: t('common.cancelTransaction'),
                                    }}
                                />

                                <DialogClose asChild>
                                    <Button
                                        testId="next"
                                        onClick={handleExitCheckoutProcess}
                                        className="mb-2"
                                    >
                                        {t('common.leave')}
                                    </Button>
                                </DialogClose>

                                <DialogClose asChild>
                                    <Button testId="previous" variant="secondary">
                                        {t('common.continue')}
                                    </Button>
                                </DialogClose>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>
            )}
            <div className="flex flex-col flex-1 w-full">{children}</div>
        </>
    )
}

export default CheckoutHeader
